// A shotgun start puts up to two groups on each hole, as slot A and slot B.
export const HOLE_COUNT = 18;
export const SLOT_LETTERS = ['A', 'B'];

export const HOLE_NUMBERS = Array.from({length: HOLE_COUNT}, (_, index) => index + 1);
export const HOLE_SLOTS = HOLE_NUMBERS.flatMap(hole => SLOT_LETTERS.map(letter => `${ hole }${ letter }`));

// Course order (1A, 1B, 2A, ... 18B), with unassigned values last.
export function compareHoleSlots(a, b) {
	if (!a || !b) {
		return (a ? 0 : 1) - (b ? 0 : 1);
	}

	const holeOrder = parseInt(a, 10) - parseInt(b, 10);

	return holeOrder !== 0 ? holeOrder : a.localeCompare(b);
}

// { groupId: slot } for every group that has a hole, read off its players.
export function assignmentsFromPlayers(players) {
	const assignments = {};

	players.forEach(({groupId, holeAssignment}) => {
		if (holeAssignment && !assignments[groupId]) {
			assignments[groupId] = holeAssignment;
		}
	});

	return assignments;
}

// { slot: groupId }, so the board can look up who is on each slot.
export function groupIdsBySlot(assignments) {
	const bySlot = {};

	Object.entries(assignments).forEach(([groupId, slot]) => {
		if (slot) {
			bySlot[slot] = groupId;
		}
	});

	return bySlot;
}

// Puts a group on a slot. A group already on that slot swaps into the moving
// group's old slot, or goes back to unassigned if the moving group had none.
export function assignGroup(assignments, groupId, slot) {
	const occupant = groupIdsBySlot(assignments)[slot];

	if (occupant === groupId) {
		return assignments;
	}

	const next = {...assignments, [groupId]: slot};

	if (occupant) {
		if (assignments[groupId]) {
			next[occupant] = assignments[groupId];
		} else {
			delete next[occupant];
		}
	}

	return next;
}

export function unassignGroup(assignments, groupId) {
	const next = {...assignments};

	delete next[groupId];

	return next;
}

// Places unassigned groups (in the order given) on open slots, filling the
// emptiest, lowest-numbered hole first so every hole gets a group before any
// hole gets a second.
export function autoFillAssignments(groupIds, assignments) {
	const next = {...assignments};
	const bySlot = groupIdsBySlot(next);
	const filledSlots = [];
	const groupsOnHole = hole => SLOT_LETTERS.filter(letter => bySlot[`${ hole }${ letter }`]).length;

	groupIds.filter(groupId => !next[groupId]).forEach(groupId => {
		let bestHole = null;

		HOLE_NUMBERS.forEach(hole => {
			const count = groupsOnHole(hole);

			if (count < SLOT_LETTERS.length && (bestHole === null || count < groupsOnHole(bestHole))) {
				bestHole = hole;
			}
		});

		if (bestHole === null) {
			return;
		}

		const letter = SLOT_LETTERS.find(openLetter => !bySlot[`${ bestHole }${ openLetter }`]);
		const slot = `${ bestHole }${ letter }`;

		next[groupId] = slot;
		bySlot[slot] = groupId;
		filledSlots.push(slot);
	});

	return {assignments: next, filledSlots};
}

// The groups whose slot differs from what is saved. null means "clear the hole".
export function changedAssignments(saved, draft) {
	const changes = {};
	const groupIds = new Set([...Object.keys(saved), ...Object.keys(draft)]);

	groupIds.forEach(groupId => {
		const before = saved[groupId] || null;
		const after = draft[groupId] || null;

		if (before !== after) {
			changes[groupId] = after;
		}
	});

	return changes;
}
